import React, { useState, useEffect } from "react";
// import { fetchEmployees, fetchCustomers } from "../../Utils/api";
import { useAuth } from "../../Auth/AuthContext";
import { fetchGateCodes, createGateCode, updateGateCode, deleteGateCode } from "../../Utils/api";
import {
  TextField,
  InputAdornment,
  Button,
  Grid,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Radio,
  RadioGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  FormControlLabel,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Divider, List, ListItem, ListItemText, CircularProgress } from "@mui/material";
import "./customer-form.css";
import CloseIcon from "@mui/icons-material/Close";
import { US_STATES } from "../../Utils/constants";

const STATE_ABBREVS = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA",
  Colorado: "CO", Connecticut: "CT", Delaware: "DE", Florida: "FL", Georgia: "GA",
  Hawaii: "HI", Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA",
  Kansas: "KS", Kentucky: "KY", Louisiana: "LA", Maine: "ME", Maryland: "MD",
  Massachusetts: "MA", Michigan: "MI", Minnesota: "MN", Mississippi: "MS", Missouri: "MO",
  Montana: "MT", Nebraska: "NE", Nevada: "NV", "New Hampshire": "NH", "New Jersey": "NJ",
  "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND",
  Ohio: "OH", Oklahoma: "OK", Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI",
  "South Carolina": "SC", "South Dakota": "SD", Tennessee: "TN", Texas: "TX",
  Utah: "UT", Vermont: "VT", Virginia: "VA", Washington: "WA", "West Virginia": "WV",
  Wisconsin: "WI", Wyoming: "WY",
};

const CustomerModal = ({ open, onClose, onCustomerEdit, initialData }) => {
  // state variables ->
  const [step, setStep] = useState(1);
  const [isNextDisabled, setIsNextDisabled] = useState(true);
  const [showPoolRequired, setShowPoolRequired] = useState(false);
  const [volume, setVolume] = useState(null);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [emailError, setEmailError] = useState("");
  const { companyId } = useAuth();

  const poolTypesDD = [
    { value: "RESIDENTIAL", label: "Residential" },
    { value: "COMMERCIAL", label: "Commercial" },
    { value: "POOL", label: "Pool" },
    { value: "POOLSPA", label: "Pool+Spa" },
  ];

  const [sameEmailError, setSameEmailError] = useState("");

  // Gate code state
  const [gateCodes, setGateCodes] = useState([]);
  const [gateCodeLoading, setGateCodeLoading] = useState(false);
  const [gateCodeError, setGateCodeError] = useState("");
  const [showGateCodeForm, setShowGateCodeForm] = useState(false);
  const [editingGateCode, setEditingGateCode] = useState(null);
  const [draftType, setDraftType] = useState("");
  const [draftCode, setDraftCode] = useState("");
  const [draftNotes, setDraftNotes] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    email: "",
    secondaryEmail: "",
    status: "Y",
  });

  const [poolData, setPoolData] = useState({
    poolType: "",
    poolLength: "",
    poolWidth: "",
    poolHeight: "",
    poolShape: "",
    poolCapacity: "",
  });

  useEffect(() => {
    if (initialData) {
      const { customer, poolDetails } = initialData;

      // Clean phone number: remove +1 prefix if present
      let cleanedPhoneNumber = customer?.custContactNo || "";
      if (cleanedPhoneNumber.startsWith("+1")) {
        cleanedPhoneNumber = cleanedPhoneNumber.substring(2);
      }
      // Also handle cases with spaces or dashes
      cleanedPhoneNumber = cleanedPhoneNumber.replace(/[\s\-\(\)]/g, "");
      // Keep only digits
      cleanedPhoneNumber = cleanedPhoneNumber.replace(/\D/g, "");

      setFormData({
        firstName: customer?.custFirstName || "",
        lastName: customer?.custLastName || "",
        phoneNumber: cleanedPhoneNumber,
        address: customer?.custAddress || "",
        city: customer?.custCity || "",
        state: customer?.custState || "",
        zipCode: customer?.custZip || "",
        email: customer?.custEmail || "",
        secondaryEmail: customer?.secondaryEmail || "",
        status: customer?.currRecInd || "Y",
      });
      setPoolData({
        poolType: poolDetails?.poolType || "",
        poolLength: poolDetails?.poolLength
          ? parseFloat(poolDetails.poolLength)
          : "",
        poolWidth: poolDetails?.poolWidth
          ? parseFloat(poolDetails.poolWidth)
          : "",
        poolHeight: poolDetails?.poolHeight
          ? parseFloat(poolDetails.poolHeight)
          : "",
        poolShape: poolDetails?.poolShape || "",
        poolCapacity: poolDetails?.poolCapacity
          ? parseFloat(poolDetails.poolCapacity)
          : "",
      });

      // Load gate codes for this customer
      const customerId = customer?.customerId;
      if (customerId) {
        setGateCodeLoading(true);
        setGateCodeError("");
        fetchGateCodes(customerId)
          .then((codes) => setGateCodes(codes || []))
          .catch(() => setGateCodeError("Could not load gate codes."))
          .finally(() => setGateCodeLoading(false));
      }
    }
  }, [initialData]);

  const handleSubmit = async () => {
    try {
      const customerData = {
        customerDetails: {
          ...initialData.customer,
          companyId: companyId,
          custFirstName: formData.firstName,
          custLastName: formData.lastName,
          custEmail: formData.email,
          secondaryEmail: formData.secondaryEmail.trim() || null,
          custAddress: formData.address,
          custCity: formData.city,
          custState: formData.state,
          custZip: formData.zipCode,
          custContactNo: formData.phoneNumber,
          customerId: initialData.customer.customerId,
          currRecInd: formData.status,
        },
        poolDetails: {
          ...initialData.poolDetails,
          companyId: companyId,
          poolType: poolData.poolType,
          poolLength: parseFloat(poolData.poolLength),
          poolWidth: parseFloat(poolData.poolWidth),
          poolHeight: parseFloat(poolData.poolHeight),
          poolShape: poolData.poolShape,
          poolCapacity: parseFloat(poolData.poolCapacity),
        },
      };
      onCustomerEdit(customerData);
      onClose();
    } catch (error) {
      console.error("Failed to edit customer:", error);
      // Error will be handled by parent component
    }
  };

  // Calculate pool volume in gallons
  useEffect(() => {
    const { poolLength, poolWidth, poolHeight, poolShape } = poolData;
    if (poolLength && poolWidth && poolHeight && poolShape) {
      const baseVolume = parseFloat(poolLength) * parseFloat(poolWidth) * parseFloat(poolHeight) * 7.48;

      // Apply shape-specific multipliers
      let shapeMultiplier = 1.0;
      const shapeLower = poolShape.toLowerCase();

      if (shapeLower === 'rectangle' || shapeLower === 'square') {
        shapeMultiplier = 1.0;
      } else if (shapeLower === 'oval') {
        shapeMultiplier = 0.89;
      } else if (shapeLower === 'circle') {
        shapeMultiplier = 0.78;
      } else if (shapeLower === 'kidney') {
        shapeMultiplier = 0.75;
      } else if (shapeLower === 'l-shape') {
        shapeMultiplier = 0.85;
      } else if (shapeLower === 'figure-8') {
        shapeMultiplier = 0.75;
      } else if (shapeLower === 'roman') {
        shapeMultiplier = 0.90;
      } else if (shapeLower === 'freeform') {
        shapeMultiplier = 0.80;
      }

      const calculated = Math.round(baseVolume * shapeMultiplier * 100) / 100;
      setVolume(calculated);
      setPoolData((prev) => ({ ...prev, poolCapacity: calculated }));
    } else {
      setVolume(null);
      setPoolData((prev) => ({ ...prev, poolCapacity: '' }));
    }
  }, [poolData.poolLength, poolData.poolWidth, poolData.poolHeight, poolData.poolShape]);

  // Handle next step
  const handleNext = () => {
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      handleSubmit();
    }
  };

  // Handle previous step
  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const checkSameEmail = (email, secondaryEmail) => {
    const e = email.trim().toLowerCase();
    const s = secondaryEmail.trim().toLowerCase();
    if (e && s && e === s) {
      setSameEmailError("Primary and secondary email must be different.");
    } else {
      setSameEmailError("");
    }
  };

  const handleChange = (e) => {
    const updated = { ...formData, [e.target.name]: e.target.value };
    setFormData(updated);
    if (e.target.name === 'email' || e.target.name === 'secondaryEmail') {
      checkSameEmail(
        e.target.name === 'email' ? e.target.value : updated.email,
        e.target.name === 'secondaryEmail' ? e.target.value : updated.secondaryEmail
      );
    }
  };
  // Gate code handlers
  const handleBeginAddGateCode = () => {
    setEditingGateCode(null);
    setDraftType("");
    setDraftCode("");
    setDraftNotes("");
    setShowGateCodeForm(true);
  };

  const handleBeginEditGateCode = (code) => {
    setEditingGateCode(code);
    setDraftType(code.gateCodeType);
    setDraftCode(code.gateCode);
    setDraftNotes(code.gateCodeNotes || "");
    setShowGateCodeForm(true);
  };

  const handleSaveGateCode = async () => {
    const customerId = initialData?.customer?.customerId;
    if (!customerId || !draftCode.trim()) return;
    const payload = {
      gateCodeType: draftType.trim() || "Main",
      gateCode: draftCode.trim(),
      gateCodeNotes: draftNotes.trim() || null,
    };
    setGateCodeLoading(true);
    try {
      if (editingGateCode) {
        const updated = await updateGateCode(customerId, editingGateCode.gateCodeId, payload);
        setGateCodes(prev => prev.map(c => c.gateCodeId === editingGateCode.gateCodeId ? updated : c));
      } else {
        const created = await createGateCode(customerId, payload);
        setGateCodes(prev => [...prev, created]);
      }
      setShowGateCodeForm(false);
      setEditingGateCode(null);
    } catch {
      setGateCodeError("Could not save gate code.");
    } finally {
      setGateCodeLoading(false);
    }
  };

  const handleDeleteGateCode = async (gateCodeId) => {
    const customerId = initialData?.customer?.customerId;
    if (!customerId) return;
    setGateCodeLoading(true);
    try {
      await deleteGateCode(customerId, gateCodeId);
      setGateCodes(prev => prev.filter(c => c.gateCodeId !== gateCodeId));
      setDeleteConfirmId(null);
    } catch {
      setGateCodeError("Could not delete gate code.");
    } finally {
      setGateCodeLoading(false);
    }
  };

  //pool shape options
  const shapeOptions = [
    "Circle",
    "Square",
    "Rectangle",
    "Oval",
    "Kidney",
    "L-Shape",
    "Figure-8",
    "Roman",
    "Freeform",
  ];
  //error function for invalid pool shape
  const validateShape = (inputValue) => {
    if (!shapeOptions.includes(inputValue)) {
      setError(true);
      setErrorMessage("The entered shape does not match our options");
      return false;
    }
    setError(false);
    setErrorMessage("");
    return true;
  };

  useEffect(() => {
    let isValid = false;
    const hasTouchedPool =
      poolData.poolType !== "" ||
      poolData.poolLength !== "" ||
      poolData.poolWidth !== "" ||
      poolData.poolHeight !== "" ||
      poolData.poolShape !== "";

    if (step === 2 && hasTouchedPool) {
      setShowPoolRequired(true);
    }
    const isValidEmail =
      /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
        formData.email.trim(),
      );
    if (step === 1) {
      isValid =
        formData.firstName.trim() &&
        formData.lastName.trim() &&
        formData.phoneNumber.trim().length === 10 &&
        formData.address.trim() &&
        formData.city.trim() &&
        formData.state.trim() &&
        formData.zipCode.trim().length === 5 &&
        isValidEmail &&
        !sameEmailError;
    } else if (step === 2) {
      // 'all or nothing' validation for pool details form
      const allPoolFieldsFilled =
        poolData.poolType.trim() &&
        poolData.poolLength !== null &&
        poolData.poolLength !== "" &&
        poolData.poolWidth !== null &&
        poolData.poolWidth !== "" &&
        poolData.poolHeight !== null &&
        poolData.poolHeight !== "" &&
        poolData.poolShape.trim();

      isValid = !hasTouchedPool || allPoolFieldsFilled;
      setShowPoolRequired(!!hasTouchedPool);
    } else {
      isValid = true;
    }
    setIsNextDisabled(!isValid);
  }, [step, formData, poolData, sameEmailError]);

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div>
            <Grid container spacing={2}>
              {/* First Name */}
              <Grid item xs={6}>
                <TextField
                  label="First Name"
                  name="firstName"
                  required
                  fullWidth
                  value={formData.firstName}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Only letters and spaces allowed."
                />
              </Grid>

              {/* Last Name */}
              <Grid item xs={6}>
                <TextField
                  label="Last Name"
                  name="lastName"
                  required
                  fullWidth
                  value={formData.lastName}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Only letters and spaces allowed."
                />
              </Grid>

              {/* Phone Number */}
              <Grid item xs={6}>
                <TextField
                  label="Phone Number"
                  name="phoneNumber"
                  required
                  fullWidth
                  value={formData.phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,10}$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  error={
                    formData.phoneNumber.length > 0 &&
                    formData.phoneNumber.length !== 10
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    ),
                  }}
                  helperText={"10-digit number only."}
                />
              </Grid>

              {/* Address */}
              <Grid item xs={6}>
                <TextField
                  label="Address"
                  name="address"
                  required
                  fullWidth
                  value={formData.address}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* City */}
              <Grid item xs={4}>
                <TextField
                  label="City"
                  name="city"
                  required
                  fullWidth
                  value={formData.city}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Only letters and spaces are allowed."
                />
              </Grid>

              {/* State */}
              <Grid item xs={4}>
                <Autocomplete
                  options={US_STATES}
                  value={formData.state || null}
                  onChange={(event, newValue) => {
                    handleChange({ target: { name: "state", value: newValue || "" } });
                  }}
                  filterOptions={(options, { inputValue }) => {
                    const input = inputValue.trim().toLowerCase();
                    return options.filter(
                      (s) =>
                        s.toLowerCase().includes(input) ||
                        (STATE_ABBREVS[s] || "").toLowerCase().startsWith(input),
                    );
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option}>
                      {STATE_ABBREVS[option]} — {option}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      name="state"
                      required
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <LocationOnIcon />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      helperText="Type state name or abbreviation (e.g. TX)."
                    />
                  )}
                />
              </Grid>

              {/* Zip Code */}
              <Grid item xs={4}>
                <TextField
                  label="Zip Code"
                  name="zipCode"
                  required
                  fullWidth
                  value={formData.zipCode}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,5}$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  error={
                    formData.zipCode.length > 0 && formData.zipCode.length !== 5
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon />
                      </InputAdornment>
                    ),
                  }}
                  helperText="5-digit number only."
                />
              </Grid>

              {/* Gate Codes */}
              {initialData && (
                <Grid item xs={12}>
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#274070', letterSpacing: 0.5 }}>
                        GATE CODES
                      </Typography>
                      <IconButton size="small" onClick={handleBeginAddGateCode} disabled={gateCodeLoading}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {gateCodeLoading && <CircularProgress size={20} sx={{ display: 'block', mx: 'auto', my: 1 }} />}
                    {gateCodeError && <Typography variant="caption" color="error">{gateCodeError}</Typography>}
                    {!gateCodeLoading && gateCodes.length === 0 && (
                      <Typography variant="body2" color="text.secondary">No gate codes added.</Typography>
                    )}
                    {gateCodes.length > 0 && (
                      <List dense disablePadding>
                        {gateCodes.map((code, idx) => (
                          <React.Fragment key={code.gateCodeId}>
                            {idx > 0 && <Divider />}
                            <ListItem
                              disableGutters
                              secondaryAction={
                                <Box>
                                  <IconButton size="small" onClick={() => handleBeginEditGateCode(code)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(code.gateCodeId)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              }
                            >
                              <ListItemText
                                primary={<><strong>{code.gateCode}</strong> <Typography component="span" variant="caption" color="text.secondary">({code.gateCodeType})</Typography></>}
                                secondary={code.gateCodeNotes || undefined}
                              />
                            </ListItem>
                          </React.Fragment>
                        ))}
                      </List>
                    )}

                    {/* Inline add/edit form */}
                    {showGateCodeForm && (
                      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Divider />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {editingGateCode ? 'Edit Gate Code' : 'Add Gate Code'}
                        </Typography>
                        <TextField
                          label="Type"
                          placeholder="Main"
                          size="small"
                          fullWidth
                          value={draftType}
                          onChange={(e) => setDraftType(e.target.value)}
                        />
                        <TextField
                          label="Gate Code"
                          size="small"
                          fullWidth
                          value={draftCode}
                          onChange={(e) => { if (e.target.value.length <= 30) setDraftCode(e.target.value); }}
                          helperText={`${draftCode.length}/30`}
                        />
                        <TextField
                          label="Notes (optional)"
                          size="small"
                          fullWidth
                          value={draftNotes}
                          onChange={(e) => setDraftNotes(e.target.value)}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button size="small" onClick={() => { setShowGateCodeForm(false); setEditingGateCode(null); }}>
                            Cancel
                          </Button>
                          <Button size="small" variant="contained" onClick={handleSaveGateCode} disabled={!draftCode.trim()}>
                            Save
                          </Button>
                        </Box>
                      </Box>
                    )}

                    {/* Delete confirmation */}
                    {deleteConfirmId !== null && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#fff3e0', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Delete this gate code?</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" color="error" variant="contained" onClick={() => handleDeleteGateCode(deleteConfirmId)}>
                            Delete
                          </Button>
                          <Button size="small" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Email Address */}
              <Grid item xs={12}>
                <TextField
                  label="Email Address"
                  name="email"
                  required
                  fullWidth
                  autoComplete="email"
                  value={formData.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z0-9@._%+\-]*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (
                      value &&
                      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
                        value,
                      )
                    ) {
                      setEmailError("Invalid email format");
                    } else {
                      setEmailError("");
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                  error={
                    formData.email &&
                    !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
                      formData.email,
                    )
                  }
                  helperText={
                    formData.email &&
                    !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
                      formData.email,
                    )
                      ? "Please enter a valid email address."
                      : "You will receive an email after successful update."
                  }
                />
              </Grid>

              {/* Secondary Email */}
              <Grid item xs={12}>
                <TextField
                  label="Secondary Email (Optional)"
                  name="secondaryEmail"
                  fullWidth
                  autoComplete="email"
                  value={formData.secondaryEmail}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z0-9@._%+\-]*$/.test(value)) handleChange(e);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                  error={
                    !!sameEmailError ||
                    (!!formData.secondaryEmail.trim() &&
                    !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.secondaryEmail.trim()))
                  }
                  helperText={
                    sameEmailError ||
                    (formData.secondaryEmail.trim() &&
                    !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.secondaryEmail.trim())
                      ? "Please enter a valid email address."
                      : "Optional secondary contact email.")
                  }
                />
              </Grid>

              {/* Activity Status*/}
              <Grid item xs={12} marginLeft={1}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ minWidth: "60px", fontWeight: 500 }}
                  >
                    Status:
                  </Typography>
                  <RadioGroup
                    row
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    sx={{ display: "flex", flexDirection: "row", gap: 2 }}
                  >
                    <FormControlLabel
                      value="Y"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">Active</Typography>}
                    />
                    <FormControlLabel
                      value="N"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">Inactive</Typography>}
                    />
                  </RadioGroup>
                </Box>
              </Grid>
            </Grid>
          </div>
        );
      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>
                  Pool Type{" "}
                  {showPoolRequired && <span style={{ color: "red" }}>*</span>}
                </InputLabel>
                <Select
                  label="Pool Type"
                  name="poolType"
                  value={poolData.poolType}
                  onChange={(e) =>
                    setPoolData({ ...poolData, poolType: e.target.value })
                  }
                  sx={{ "& .MuiInputBase-input": { textAlign: "left" } }}
                  startAdornment={
                    <InputAdornment position="start"></InputAdornment>
                  }
                >
                  {/* Render dropdown options, added a none option now */}
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {poolTypesDD.map((option, index) => (
                    <MenuItem value={option.value} key={index}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={
                  <>
                    Length (ft){" "}
                    {showPoolRequired && (
                      <span style={{ color: "red" }}>*</span>
                    )}
                  </>
                }
                name="length"
                fullWidth
                value={poolData.poolLength}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    setPoolData({ ...poolData, poolLength: e.target.value });
                  }
                }}
                InputLabelProps={{ shrink: true }}
                helperText="Enter a number (e.g., 10 or 10.5)."
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={
                  <>
                    Width (ft){" "}
                    {showPoolRequired && (
                      <span style={{ color: "red" }}>*</span>
                    )}
                  </>
                }
                name="width"
                fullWidth
                value={poolData.poolWidth}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    setPoolData({ ...poolData, poolWidth: e.target.value });
                  }
                }}
                InputLabelProps={{ shrink: true }} // Ensures label stays on top
                helperText="Enter a number (e.g., 10 or 10.5)."
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={
                  <>
                    Average Depth (ft){" "}
                    {showPoolRequired && (
                      <span style={{ color: "red" }}>*</span>
                    )}
                  </>
                }
                name="height"
                fullWidth
                value={poolData.poolHeight}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    setPoolData({ ...poolData, poolHeight: e.target.value });
                  }
                }}
                InputLabelProps={{ shrink: true }} // Ensures label stays on top
                helperText="Enter a number (e.g., 10 or 10.5)."
              />
            </Grid>

            <Grid item xs={4}>
              <Typography variant="body1" sx={{ marginTop: "10px", fontWeight: "bold" }}>
                Calculated Volume:
              </Typography>
              <Typography variant="h6" color="primary">
                {volume !== null ? `${volume.toFixed(2)} gallons` : "-"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={shapeOptions}
                value={poolData.poolShape}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    if (/^[A-Za-z\s-]*$/.test(newInputValue)) {
                      // Updated regex to allow hyphens
                      const syntheticEvent = {
                        target: {
                          name: "shape",
                          value: newInputValue,
                        },
                      };
                      setPoolData((prev) => ({
                        ...prev,
                        poolShape: newInputValue || "",
                      }));
                      validateShape(newInputValue || "");
                    }
                  }
                }}
                onChange={(event, newValue) => {
                  setPoolData((prev) => ({
                    ...prev,
                    poolShape: newValue || "",
                  }));
                  validateShape(newValue || "");
                }}
                onBlur={() => {
                  validateShape(poolData.poolShape);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      <>
                        Shape{" "}
                        {showPoolRequired && (
                          <span style={{ color: "red" }}>*</span>
                        )}
                      </>
                    }
                    name="shape"
                    fullWidth
                    error={error}
                    helperText={
                      error
                        ? errorMessage
                        : "Enter or choose from the list of shapes."
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          fontWeight: "bold",
          textAlign: "center",
          position: "relative",
        }}
      >
        {step === 1
          ? (initialData ? "Edit Customer Details" : "Add Customer")
          : step === 2
            ? (initialData ? "Edit Pool Details" : "Add Pool Details")
            : (initialData ? "Edit Technician" : "Assign Technician")}

        {/* CLOSE BUTTON */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#666",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "24px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            marginTop: "10px",
          }}
        >
          {renderStepContent()}

          {/* Navigation Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "20px",
            }}
          >
            {step === 1 ? (
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
            ) : (
              <Button variant="outlined" onClick={handleBack}>
                Back
              </Button>
            )}
            <Tooltip
              title={
                isNextDisabled
                  ? "Please fill all required (*) fields before proceeding"
                  : ""
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  onClick={step === 2 ? handleSubmit : handleNext}
                  disabled={isNextDisabled}
                >
                  {step === 2 ? "Submit" : "Next"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerModal;
